import { Fragment, useEffect } from "react";
import { Transition } from "@headlessui/react";
import {
  CheckmarkFilled,
  Information,
  CloseFilled,
  Close,
  WarningFilled,
} from "@carbon/icons-react";

export type ToastType = "success" | "error" | "info" | "warning";

type ToastProps = {
  show: boolean;
  message: string;
  type?: ToastType;
  duration?: number; // Auto-dismiss duration in ms (0 = no auto-dismiss)
  onClose: () => void;
};

const Toast = ({
  show,
  message,
  type = "info",
  duration = 5000,
  onClose,
}: ToastProps) => {
  // Auto-dismiss after duration
  useEffect(() => {
    if (show && duration > 0) {
      const timer = setTimeout(() => {
        onClose();
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [show, duration, onClose]);

  const getStyles = () => {
    switch (type) {
      case "success":
        return {
          bg: "bg-green-700",
          icon: (
            <CheckmarkFilled
              size={24}
              className="text-green-200"
              aria-hidden="true"
            />
          ),
        };
      case "error":
        return {
          bg: "bg-red-700",
          icon: (
            <CloseFilled size={24} className="text-red-200" aria-hidden="true" />
          ),
        };
      case "warning":
        return {
          bg: "bg-yellow-600",
          icon: (
            <WarningFilled
              size={24}
              className="text-yellow-200"
              aria-hidden="true"
            />
          ),
        };
      case "info":
      default:
        return {
          bg: "bg-blue-700",
          icon: (
            <Information
              size={24}
              className="text-blue-200"
              aria-hidden="true"
            />
          ),
        };
    }
  };

  const styles = getStyles();

  return (
    <Fragment>
      <div
        aria-live="assertive"
        className="z-50 fixed top-14 inset-0 flex items-end px-4 py-6 pointer-events-none sm:p-6 sm:items-start"
      >
        <div className="w-full flex flex-col items-center space-y-4 sm:items-end">
          <Transition
            show={show}
            as={Fragment}
            enter="transform ease-out duration-300 transition"
            enterFrom="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2"
            enterTo="translate-y-0 opacity-100 sm:translate-x-0"
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div
              className={`max-w-sm md:max-w-md w-full ${styles.bg} shadow-lg rounded pointer-events-auto ring-1 ring-black ring-opacity-5 overflow-hidden`}
            >
              <div className="p-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0">{styles.icon}</div>
                  <div className="ml-3 w-0 flex-1 pt-0.5">
                    <p className="text-sm font-medium text-white whitespace-pre-wrap">
                      {message}
                    </p>
                  </div>
                  <div className="ml-4 flex-shrink-0 flex">
                    <button
                      className={`${styles.bg} rounded inline-flex text-white hover:text-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white`}
                      onClick={onClose}
                    >
                      <span className="sr-only">Close</span>
                      <Close size={20} aria-hidden="true" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </Transition>
        </div>
      </div>
    </Fragment>
  );
};

export default Toast;
